// v6
// Path: frontend/src/components/SyncHub/ApplyStandardMappingsModal.jsx
// Inputs: token, group, onClose, onApplied
// Outputs: React JSX modal for previewing and applying standard field mappings en masse to all pipelines in a connection group
// Summary: Universal batch application interface deploying chosen standard field mapping preset sets across cataloged data containers with prominent preset set selection, default set indication, multi-target support, and generalized container linking.

import React, { useState, useEffect } from 'react';
import { getEntityMeta } from './entityCatalogs';
import { useSyncStore } from './useSyncStore';

// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

export const ApplyStandardMappingsModal = ({ token, group, onClose, onApplied }) => {
    const [loading, setLoading] = useState(true);
    const [applying, setApplying] = useState(false);
    const [standardMappings, setStandardMappings] = useState([]);
    const [availableSets, setAvailableSets] = useState([]);
    const [setsMeta, setSetsMeta] = useState([]);
    const [selectedSet, setSelectedSet] = useState('');
    const [overwrite, setOverwrite] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [sourceSystem, setSourceSystem] = useState(group.sourceAppName || group.sourceAppCode || group.sourceSystem || 'Passport');
    const [availableSystems, setAvailableSystems] = useState([group.sourceAppName || group.sourceAppCode || group.sourceSystem || 'Passport']);
    const [resultMessage, setResultMessage] = useState(null);

    const targetSystem = group.targetAppName || group.targetAppCode || group.targetSystem || 'QuickBooks';
    const pipelines = group.pipelines || [];

    useEffect(() => {
        const fetchStandardMappings = async () => {
            setLoading(true);
            try {
                const setParam = selectedSet ? `&setName=${encodeURIComponent(selectedSet)}` : '';
                const res = await fetch(`/api/sync/standard-mappings?sourceSystem=${encodeURIComponent(sourceSystem)}&targetSystem=${encodeURIComponent(targetSystem)}${setParam}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await unbox(res);
                    const fetched = Array.isArray(data.mappings) ? data.mappings : [];
                    setStandardMappings(fetched);
                    if (Array.isArray(data.setsMeta)) {
                        setSetsMeta(data.setsMeta);
                    }
                    if (Array.isArray(data.availableSets) && data.availableSets.length > 0) {
                        setAvailableSets(data.availableSets);
                        if (!selectedSet || !data.availableSets.includes(selectedSet)) {
                            const targetDefault = data.defaultSetName || data.availableSets[0];
                            setSelectedSet(targetDefault);
                        }
                    }
                    if (Array.isArray(data.availableSourceSystems) && data.availableSourceSystems.length > 0) {
                        setAvailableSystems(data.availableSourceSystems);
                    } else if (Array.isArray(data.sourceSystems) && data.sourceSystems.length > 0) {
                        setAvailableSystems(data.sourceSystems);
                    }
                    // Default to selecting all target entities available
                    setSelectedEntities(fetched.map(m => m.TargetQBEntity || m.EntityType));
                }
            } catch (err) {
                console.error('[ApplyStandardMappingsModal] Error fetching standard mappings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchStandardMappings();
    }, [token, sourceSystem, targetSystem, selectedSet, group]);

    const handleToggleSelectAll = (checked) => {
        if (checked) {
            setSelectedEntities(standardMappings.map(m => m.TargetQBEntity || m.EntityType));
        } else {
            setSelectedEntities([]);
        }
    };

    const handleToggleEntity = (entName) => {
        setSelectedEntities(prev =>
            prev.includes(entName) ? prev.filter(e => e !== entName) : [...prev, entName]
        );
    };

    const handleApply = async () => {
        if (selectedEntities.length === 0) {
            useSyncStore.getState().showAlert('Please select at least one target entity to apply standard mappings.');
            return;
        }

        setApplying(true);
        setResultMessage(null);

        try {
            const res = await fetch('/api/sync/connection-group/apply-standard-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    connectionGroupId: group.connectionGroupId,
                    sourceConnPrefix: group.sourceConnPrefix,
                    sourceDatabaseName: group.sourceDatabase,
                    accountId: group.accountId,
                    sourceContainerId: group.sourceContainerId || group.SourceContainerID,
                    targetContainerId: group.targetContainerId || group.TargetContainerID,
                    setName: selectedSet,
                    sourceSystem,
                    targetSystem,
                    overwrite,
                    selectedEntities
                })
            });

            const data = await unbox(res);
            if (res.ok && data.success) {
                setResultMessage({
                    type: 'success',
                    text: data.message || `Successfully applied standard mappings from set "${selectedSet}": ${data.updatedCount} updated, ${data.createdCount} initialized.`,
                    details: data.details
                });
                if (onApplied) onApplied();
            } else {
                setResultMessage({
                    type: 'error',
                    text: data.error || 'Failed to apply standard mappings.'
                });
            }
        } catch (err) {
            setResultMessage({
                type: 'error',
                text: err.message
            });
        } finally {
            setApplying(false);
        }
    };

    const sourceLabel = group.sourceContainerName || group.sourceDatabase || 'Source';
    const targetLabel = group.targetContainerName || group.accountName || 'Target';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-850">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                            <span className="material-symbols-outlined text-[24px]">batch_prediction</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-white tracking-wide">
                                    Apply Standard Mappings ({targetSystem})
                                </h3>
                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-purple-900/60 text-purple-300 border border-purple-700/60 font-mono">
                                    ID: {group.connectionGroupId || group.groupKey}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">
                                Group: <strong className="text-slate-200">{group.groupName || sourceLabel}</strong> (<span className="text-sky-300 font-mono">{sourceLabel}</span> ➔ <span className="text-emerald-300 font-mono">{targetLabel}</span>)
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Body Content */}
                <div className="p-6 overflow-y-auto flex-1 space-y-5">
                    {/* Prominent Mapping Preset Set Selector Banner */}
                    <div className="bg-slate-800/90 border border-slate-700 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4 shadow-inner">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                                <span className="material-symbols-outlined text-[22px]">bookmark</span>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-300 block uppercase tracking-wider">
                                    Select Mapping Preset Set to Apply
                                </label>
                                <div className="flex items-center gap-2 mt-1">
                                    <select
                                        value={selectedSet}
                                        onChange={e => setSelectedSet(e.target.value)}
                                        className="bg-slate-900 border border-amber-500/60 rounded-lg px-3 py-1.5 text-sm text-amber-300 font-bold focus:border-amber-400 focus:outline-none shadow-sm min-w-[180px]"
                                    >
                                        {availableSets.map(s => {
                                            const isDef = setsMeta.find(m => m.setName === s)?.isDefault;
                                            return (
                                                <option key={s} value={s}>
                                                    {s}{isDef ? ' ★ (Default)' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <span className="text-xs text-slate-400">
                                        ({standardMappings.length} preset{standardMappings.length !== 1 ? 's' : ''} defined in this set)
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Systems Info */}
                        <div className="flex items-center gap-3 text-xs bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-750">
                            <div className="flex items-center gap-1.5">
                                <span className="text-slate-400">Source:</span>
                                <strong className="text-indigo-300">{sourceSystem}</strong>
                            </div>
                            <span className="text-slate-600">&rarr;</span>
                            <div className="flex items-center gap-1.5">
                                <span className="text-slate-400">Target:</span>
                                <strong className="text-emerald-300">{targetSystem}</strong>
                            </div>
                        </div>
                    </div>

                    {/* Result Notification */}
                    {resultMessage && (
                        <div className={`p-4 rounded-xl border text-xs flex flex-col gap-2 ${resultMessage.type === 'success' ? 'bg-emerald-950/50 border-emerald-500/40 text-emerald-200' : 'bg-red-950/50 border-red-500/40 text-red-200'}`}>
                            <div className="flex items-center gap-2 font-bold">
                                <span className="material-symbols-outlined text-[18px]">
                                    {resultMessage.type === 'success' ? 'check_circle' : 'error'}
                                </span>
                                <span>{resultMessage.text}</span>
                            </div>
                            {resultMessage.details && (
                                <ul className="pl-6 list-disc space-y-1 text-[11px] text-emerald-300/80 max-h-36 overflow-y-auto">
                                    {resultMessage.details.map((d, i) => (
                                        <li key={i}>
                                            <strong>{d.entityType}</strong>: {d.status === 'created' ? `Initialized new pipeline with ${d.fieldsCount} mapped fields` : `Updated existing pipeline with ${d.fieldsCount} mapped fields`}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* Mode Selection */}
                    <div className="bg-slate-850 border border-slate-700/80 rounded-xl p-4 space-y-3">
                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[16px] text-indigo-400">tune</span>
                            Application Policy
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-start gap-2.5 ${!overwrite ? 'bg-indigo-950/40 border-indigo-500/60 text-white' : 'bg-slate-900 border-slate-700/60 text-slate-400 hover:border-slate-600'}`}>
                                <input
                                    type="radio"
                                    name="applyMode"
                                    checked={!overwrite}
                                    onChange={() => setOverwrite(false)}
                                    className="mt-0.5 text-indigo-600 focus:ring-0 cursor-pointer"
                                />
                                <div className="text-xs">
                                    <span className="font-bold block text-indigo-300">Merge / Fill Missing Fields (Safe)</span>
                                    <span className="text-[11px] text-slate-400 block mt-0.5">
                                        Preserves any existing custom field mappings and only appends standard mappings for fields not yet mapped. Missing pipelines will be initialized.
                                    </span>
                                </div>
                            </label>

                            <label className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-start gap-2.5 ${overwrite ? 'bg-amber-950/40 border-amber-500/60 text-white' : 'bg-slate-900 border-slate-700/60 text-slate-400 hover:border-slate-600'}`}>
                                <input
                                    type="radio"
                                    name="applyMode"
                                    checked={overwrite}
                                    onChange={() => setOverwrite(true)}
                                    className="mt-0.5 text-amber-600 focus:ring-0 cursor-pointer"
                                />
                                <div className="text-xs">
                                    <span className="font-bold block text-amber-300">Full Overwrite / Replace</span>
                                    <span className="text-[11px] text-slate-400 block mt-0.5">
                                        Completely resets all field mappings to canonical presets across all selected entities.
                                    </span>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Target Entities Review Table */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                {targetSystem} Target Entities ({selectedEntities.length} of {standardMappings.length} selected)
                            </h4>
                            <div className="flex items-center gap-2 text-xs">
                                <button
                                    type="button"
                                    onClick={() => handleToggleSelectAll(true)}
                                    className="text-indigo-400 hover:text-indigo-300 underline"
                                >
                                    Select All
                                </button>
                                <span className="text-slate-600">|</span>
                                <button
                                    type="button"
                                    onClick={() => handleToggleSelectAll(false)}
                                    className="text-slate-400 hover:text-white underline"
                                >
                                    Deselect All
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-8 text-slate-400 text-xs">
                                <span className="material-symbols-outlined animate-spin text-2xl mb-1 text-indigo-400">sync</span>
                                <p>Loading presets for set "{selectedSet}"...</p>
                            </div>
                        ) : standardMappings.length === 0 ? (
                            <div className="p-6 text-center text-slate-400 text-xs bg-slate-800/40 rounded-xl border border-slate-700">
                                No presets found in set "{selectedSet}" for {sourceSystem} &rarr; {targetSystem}.
                            </div>
                        ) : (
                            <div className="border border-slate-700/80 rounded-xl overflow-hidden bg-slate-800/60">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-slate-850 text-slate-400 border-b border-slate-700">
                                            <th className="p-3 w-10 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEntities.length === standardMappings.length && standardMappings.length > 0}
                                                    onChange={e => handleToggleSelectAll(e.target.checked)}
                                                    className="rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-0 cursor-pointer"
                                                />
                                            </th>
                                            <th className="p-3 font-semibold">{targetSystem} Target Landing Entity</th>
                                            <th className="p-3 font-semibold">Source Table</th>
                                            <th className="p-3 font-semibold">Pipeline Status in Group</th>
                                            <th className="p-3 font-semibold">Standard Mappings</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700/60">
                                        {standardMappings.map(std => {
                                            const entName = std.TargetQBEntity || std.EntityType;
                                            const isSelected = selectedEntities.includes(entName);
                                            const meta = getEntityMeta(entName, targetSystem);
                                            
                                            // Check if a pipeline already exists in this group for this target
                                            const existing = pipelines.find(p => {
                                                const pTarget = (p.TargetQBEntity || p.EntityType || '').toLowerCase().trim();
                                                return pTarget === entName.toLowerCase().trim();
                                            });

                                            let presetCount = 0;
                                            try {
                                                const parsed = JSON.parse(std.FieldMappingsJson);
                                                presetCount = Array.isArray(parsed) ? parsed.length : 0;
                                            } catch (e) { presetCount = 0; }

                                            let currentCount = 0;
                                            if (existing) {
                                                try {
                                                    const ep = JSON.parse(existing.FieldMappingsJson);
                                                    currentCount = Array.isArray(ep) ? ep.length : 0;
                                                } catch (e) { currentCount = 0; }
                                            }

                                            return (
                                                <tr
                                                    key={entName}
                                                    onClick={() => handleToggleEntity(entName)}
                                                    className={`hover:bg-slate-750 transition-colors cursor-pointer ${isSelected ? 'bg-indigo-950/20' : ''}`}
                                                >
                                                    <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleToggleEntity(entName)}
                                                            className="rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-0 cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="p-3 font-bold text-white">
                                                        <div className="flex items-center gap-2">
                                                            <span className="material-symbols-outlined text-[17px] text-indigo-400">
                                                                {meta.icon || 'table_chart'}
                                                            </span>
                                                            <span>{entName}</span>
                                                            <span className="text-[10px] text-slate-400 font-normal">({meta.category})</span>
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-900 text-amber-300 border border-slate-750">
                                                            {std.SourceTable || meta.defaultTable || 'CUSFIL'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3">
                                                        {existing ? (
                                                            <div className="flex items-center gap-1.5 text-blue-300">
                                                                <span className="material-symbols-outlined text-[15px] text-blue-400">sync_alt</span>
                                                                <span>Existing Pipeline ({currentCount} fields)</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                                                                <span className="material-symbols-outlined text-[15px]">add_circle</span>
                                                                <span>Will Initialize New Pipeline</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="p-3">
                                                        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-800 flex items-center gap-1 w-max">
                                                            <span className="material-symbols-outlined text-[13px]">check</span>
                                                            {presetCount} Canonical Fields
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 border-t border-slate-800 bg-slate-850 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors border border-slate-700"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={applying || selectedEntities.length === 0}
                        className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-2 shadow-lg shadow-emerald-900/30"
                    >
                        {applying ? (
                            <>
                                <span className="material-symbols-outlined text-[16px] animate-spin">sync</span>
                                Applying Standards...
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-[16px]">bolt</span>
                                Apply "{selectedSet}" Standards to {selectedEntities.length} Target{selectedEntities.length !== 1 ? 's' : ''}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApplyStandardMappingsModal;

